<template>
    <div class="relative">
        <div class="fixed white" style="width: 100%;" id="tool-bar">
            <v-row class="ma-0" style="width: 100%;" align="center" justify="start">
		        <v-col cols="auto" class="d-flex flex-column justify-center mt-3">
					<slot name="breadcrumbs" :item="true"></slot>
				</v-col>
			</v-row>
            <v-layout class="mx-3 mb-3 border rounded-lg d-flex flex-row wrap" align="center">
                <!-- warehouses -->
                <v-col lg="2" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-autocomplete 
                        :label="$t('manufacturingCreateEdit.warehouse')" 
                        item-text="name"
                        item-value="id"
                        v-model="warehouseId" 
                        :items="warehouses" 
                        :clearable="false"
                        :hide-details="true"
                        :readonly="$route.name === 'show-product'"
                        @change="onWarehouseChange()"
                        @keydown-enter-prevent="save()"
                        :rules="[rules.required]"
                        :action-name="actions.GET"
                        context="warehouse"
                    ></bee-autocomplete>
                </v-col>

                <!-- material name and numebr -->
                <v-col lg="2" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-material-autocomplete 
                        :label="$t('manufacturingCreateEdit.material-name-or-num')" 
                        color="primary" 
                        v-model="materialId" 
                        :items="warehouseMaterials" 
                        :clearable="false"
                        :hide-details="true"
                        @change="onChange()"
                        @keydown-enter-prevent="save()"
                        ref="BeeAutocomplete"
                        :readonly="$route.name === 'show-product'"
                        :with-balance="true"
                        :rules="[rules.required]"
                        :action_name="actions.GET"
                        context="material"
                    ></bee-material-autocomplete>
                </v-col>

                <!-- loss ratios -->
                <v-col :lg="$emerald.ifLang('es', '2', 'auto')" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-numeric-field
                        v-model="lossRatio"
                        :label="$t('manufacturingCreateEdit.loss-ratio')"
                        :hide-details="true"
                        :readonly="true"
                        @keydown-enter-prevent="save()"
                        prefix="%"
                        :action-name="actions.EDIT"
                        context="manufacturing_bond"
                    ></bee-numeric-field>
                </v-col>

                <!-- count -->
                <v-col lg="1" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-numeric-field
                        v-model="count"
                        :label="$t('manufacturingCreateEdit.count')"
                        :disabled="false"
                        :hide-details="true"
                        :readonly="$route.name === 'show-product'"
                        @keydown-enter-prevent="save()"
                        context="manufacturing_bond"
                        :action-name="actions.EDIT"
                    ></bee-numeric-field>
                </v-col>

                <!-- producted quantity -->
                <v-col lg="2" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-numeric-field
                        v-model="producedQuantity"
                        :label="$t('manufacturingCreateEdit.produced-quantity')"
                        :disabled="false"
                        :hide-details="true"
                        :readonly="$route.name === 'show-product'"
                        @keydown-enter-prevent="save()"
                        :rules="[rules.required]"
                        context="manufacturing_bond"
                        :action-name="actions.EDIT"
                    ></bee-numeric-field>
                </v-col>

                <!-- product unit -->
                <v-col lg="1" md="4" sm="6" cols="12" class="d-flex flex-column justify-center">
                    <bee-label :content="unitSize.name"></bee-label>
                </v-col>
                <v-spacer v-if="$vuetify.breakpoint.lgAndUp"></v-spacer>

                <!-- save button -->
                <v-col cols="auto" class="d-flex align-center">
                    <bee-btn
                        v-if="$route.name !== 'show-product'"
                        :content="$route.name === 'create-product' ? $t('manufacturingCreateEdit.save'): $t('manufacturingCreateEdit.edit')"
                        outer-class="px-8"
                        color="editingColor"
                        context="manufacturing_bond"
                        :loading="loading"
                        @click="save()"
                        :action-name="actions.CREATE"
                        :disabled="isNotValid || loading || file_is_locked"
                    ></bee-btn>
                </v-col>

                <!-- back button -->
                <v-col cols="auto" class="d-flex align-center ps-0">
                    <bee-btn
                        beeicon
                        :tooltip="$t('manufacturingCreateEdit.back-to-the-bond')"
                        :icon="this.$i18n.locale === 'ar' ? 'mdi-arrow-left' : 'mdi-arrow-right'"
                        outer-class="px-0"
                        color="greyLighten3"
                        iconColor="editingColor"
                        context="manufacturing_bond"
                        @click="$router.push({
                            name: 'manufacturing-bond',
                            query:{ id: $route.query.manufacturingFileId }
                        })"
                        depressed
                        :action-name="actions.CREATE"
                        :disabled="loading"
                    ></bee-btn>
                </v-col>
            </v-layout>
        </div>
        
        <div
            v-if="this.$route.name === 'edit-product' || this.$route.name === 'show-product'"
            class="ma-auto absolute"
            id="main"
            style="width: 100%; height: auto"
        >
            <v-card flat>
                <v-card-text class="pt-0 px-3">
                    <bee-smart-table
                        max-height="65vh"
                        mode="manufacturing-raw-materials"
                        ref="table"
                        :with-color="false"
                        :zebra="true"
                        empty-state="dashes"
                        :hide-footer="true"
                        :addable="$route.name !== 'show-product' && !file_is_locked"
                        :editable="$route.name !== 'show-product' && !file_is_locked"
                        :configs="configs"
                        :loading="loading"
                        :headers="headers"
                        :items="rawMaterials || []"
                        :right-click-menu-items="rightClickMenuItmes"
                        :show-menu-actions="$route.name !== 'show-product'"
                        :messagesSelectRowValidation="messagesSelectRowValidation"
                        @row-added="addRawMaterial($event)"
                        @row-edited="updateRawMaterial($event)"
                        @cell-change="updateLocalMaterialEdit($event)"
                        @reset-data="resetEditedRow()"
                        @context-menu-delete="confirmDeleteRawMaterial($event)"
                        @nblur="handlBlurTable($event)"
                        context="manufacturing_bond"
                        :action-name="actions.CREATE"
                    >
                    </bee-smart-table>
                </v-card-text>
            </v-card>
        </div>

        <!-- fixed footer -->
        <v-bottom-navigation id="footer" app class="px-3" horizontal background-color="#E1E2E4">
            <v-row align="center" justify="center" justify-lg="end">
                <!-- balance -->
                <v-col cols="auto" lg="auto" class="ma-0 pa-0 d-flex">
                    <bee-outlined-header-info border-color="transparent" :head="$t('manufacturingCreateEdit.total_cost')" :column="false">
                        <template v-slot:content="{}">
                            <bee-money-label
                                empty-state="_"
                                :number="totalCostAmount"
                            ></bee-money-label>
                        </template>
                    </bee-outlined-header-info>
                </v-col>
            </v-row>
        </v-bottom-navigation>

        <!-- delete raw material dialog -->
        <bee-dialog
            v-model="deleteRawMaterialDialog"
            :title="$t('manufacturingCreateEdit.delete-raw-material')"
            :message="$t('manufacturingCreateEdit.are-you-sure-you-want-to-delete-the-raw-material')"
            @handler="deleteRawMaterial()"
            @close="deleteRawMaterialDialog = false"
        ></bee-dialog>
    </div>
</template>

<script>
import store from "../../../../store/index"
import EventBus from "../../../../event-bus"
import actions from "../../../../permissions/actions-names"
import rules from '../../../../validation'
import { mapState } from 'vuex'
import { entryDetectedValidation } from '../helper/validation-detected-manufacturing-entry'

export default {
    data(){
        return {
            rules: rules.rules,
            
            warehouseId: null,
            materialId: null,
            lossRatio: 0,
            totalCostAmount: 0,
            producedQuantity: null,
            count: null,
            unitSize: { id: null, name: null } ,

            tableWarehouseId: null,
            
            deleteRawMaterialDialog: false,
            deleteRawMaterialId: null,

            lastPurchasePrice: null,
            messagesSelectRowValidation: [],

            loading: false,
            actions,
        }
    },

    computed:{
        ...mapState({
            configs: state => state.configs.configs,
            materials: state => state.materials.materials,
            warehouses: state => state.warehouses.warehouses,
            unitSizes: state => state.unitSizes.unit_sizes,
            entry: state => state.manufacturing.entry,
            rawMaterials: state => state.manufacturing.rawMaterials,
        }),

        isNotValid(){
            return !this.warehouseId || !this.materialId || !this.producedQuantity;
        },

        warehouseMaterials(){
            return this.materials.filter(c=> this.warehouseId ? c.warehouse_id == this.warehouseId : true).filter(c=>c.type == 2)
        },

        tableWarehouseMaterials(){
            return this.materials
            .filter(c => this.tableWarehouseId ? c.warehouse_id == this.tableWarehouseId : true)
            .filter(c => c.type == 2)
        },
        sum_total_quantity(){ 
            return this.rawMaterials.length ? this.rawMaterials.map(c => c.quantity.quantity).reduce((a, b) => a + b) : null;
        },

        file_is_locked(){
            return this.configs?.financial_file?.is_locked
        },

        /*
        total_cost_amount(){ 
            return this.rawMaterials.length ? this.rawMaterials.map(c => c.total_cost.amount)?.reduce((a, b) => a + b) : null;
        },
        lossRatioCalculated(){
            let rawMaterialsQuantity =
                this.rawMaterials.length > 0 
                ? this.rawMaterials.map(c => c.quantity.quantity).reduce((a, b) => a + b)
                : null;
            if (!rawMaterialsQuantity || !this.producedQuantity){
                return null;
            }
            return ((rawMaterialsQuantity - this.producedQuantity) / rawMaterialsQuantity) * 100;
        },
        */

        rightClickMenuItmes() {
            return [
                {
                    name: 'edit',
                    disabled: false,
                    action_name: actions.EDIT,
                    text: this.$t('manufacturingCreateEdit.context-menu.edit')
                }, 
                {
                    name: 'context-menu-delete',
                    disabled: false,
                    action_name: actions.DELETE,
                    text: this.$t('manufacturingCreateEdit.context-menu.delete')
                },
            ]
        },

        headers() {
            return [
                {
                    text: this.$t('manufacturingCreateEdit.headers.no'),
                    value: '-',
                    type: 'auto_increment',
                    width: '15%',
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.warehouse'),
                    value: 'warehouse_id',
                    type: 'autocomplete',
                    width: '100%',
                    editable: true,
                    rules: [this.rules.required],
                    settings: {
                        items: this.warehouses,
                        itemText: 'name',
                        itemValue: 'id',
                        context: 'warehouses',
                        actionName: actions.GET
                    }
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.material'),
                    value: 'material_id',
                    type: 'materials-autocomplete',
                    width: '100%',
                    editable: true,
                    rules: [this.rules.required],
                    settings: {
                        items: this.warehouseMaterials,
                        subItems: this.tableWarehouseMaterials,
                        context: 'materials',
                        actionName: this.actions.GET
                    }
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.count'),
                    value: 'count',
                    type: 'numeric',
                    width: '100%',
                    editable: true,
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.quantity'),
                    value: 'quantity.quantity',
                    type: 'numeric',
                    width: '100%',
                    editable: true,
                    rules: [this.rules.required],
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.cost-of-one'),
                    value: 'cost',
                    type: 'money',
                    width: '100%',
                    editable: true,
                    rules: [this.rules.required],
                },
                {
                    text: this.$t('manufacturingCreateEdit.headers.cost'),
                    value: 'total_cost',
                    type: 'money',
                    width: '100%',
                    editable: true,
                    settings: { disabled: true },
                    rules: [this.rules.required],
                },
                { text: '', value: 'hint', type: 'hint', width: '10%', editable: false,},
            ]
        }
    },

    methods:{
        // entry
        save(){
            if (this.isNotValid) { return }
            
            this.loading = true;
            
            const action = this.$route.name === 'create-product' ? 'createEntry' : 'updateEntry';

            this.$store.dispatch(`manufacturing/${action}`, {
                manufacturingFileId: this.$route.query.manufacturingFileId,
                entryId: this.$route.query.entryId,
                material_id: this.materialId,
                warehouse_id: this.warehouseId,
                count: this.count,
                quantity: {
                    quantity: this.producedQuantity,
                    unit_size_id: this.unitSize.id,
                },
                unit_size_id: this.unitSize.id,
                cost: {
                    amount: 0,
                    currency_id: this.configs.financial_file.default_currency_id
                },
                loss_ratio: null
            })
            .then(async (data) => {
                if (this.$route.name === 'create-product'){
                    await this.$router.replace({
                        name: 'edit-product',
                        query: {
                            manufacturingFileId: this.$route.query.manufacturingFileId,
                            entryId: data
                        },
                    })
                    .then(() => {
                        window.observer.observe(document.querySelector("#tool-bar"));
                        window.observer.observe(document.querySelector("#footer"));
                    })
                }
                else if (this.$route.name === 'edit-product'){
                    EventBus.$emit('notification', this.$t('manufacturingCreateEdit.product-is-updated-successfully'));
                }
            })
            .finally(() => {
                this.loading = false;
            })
        },

        fetchAllRawMaterials(){
            return this.$store.dispatch('manufacturing/fetchAllRawMaterials', {
                manufacturingFileId: this.$route.query.manufacturingFileId,
                entryId: this.$route.query.entryId
            })
        },

        fetchEntryById(){
            this.$store.dispatch('manufacturing/fetchEntryById', {
                manufacturingFileId: this.$route.query.manufacturingFileId ,
                entryId: this.$route.query.entryId
            })
            .then((data) => {
                this.warehouseId = data.warehouse_id;
                this.materialId = data.material_id;
                this.producedQuantity = data.quantity.quantity;
                this.count = data.count;
                this.unitSize = this.$store.state.unitSizes.unit_sizes.find(c => c.id === this.materials.find(c => c.id === data.material_id)?.unit_size_id);
                this.lossRatio = data.loss_ratio;
                this.totalCostAmount = data.total_cost.amount;
            })
        },

        // raw material
        addRawMaterial(payload){
            this.loading = true;

            const entry = payload.value;

            const unit_size_id = this.materials.find(c => c.id === entry.material_id)?.unit_size_id
            const entryId = Number(this.$route.query.entryId);

            this.$store.dispatch('manufacturing/createRawMaterial', {
                manufacturingFileId: this.$route.query.manufacturingFileId,
                entryId,
                material_id: entry.material_id,
                warehouse_id: entry.warehouse_id,
                count: entry.count,
                quantity: {
                    quantity: entry['quantity.quantity'],
                    unit_size_id
                },
                unit_size_id,
                cost: {
                    amount: entry.cost.amount,
                    currency_id: this.configs.financial_file.default_currency_id
                },
                manufacturing_file_entry_id: entryId
            })
            .then(() => {
                payload.deleteRowData({
                    warehouse_id: this.configs.warehouse.id
                });
                Promise.all([
                    this.fetchAllRawMaterials()
                    .then(() => {
                        payload.connectionEnded(1, 'BeeAutocomplete');
                        this.loading = false;
                    }),
                    this.fetchEntryById()
                ])
            })
            .catch(() => {
                payload.connectionEnded(1, 'BeeAutocomplete');
                this.loading = false;
            })
        },

        updateRawMaterial(payload){
            const entry = this.rawMaterials[payload.rowIndex];
            this.loading = true;

            const unit_size_id = this.materials.find(c => c.id === entry.material_id )?.unit_size_id

            const entryId = this.$route.query.entryId;

            this.$store.dispatch('manufacturing/updateRawMaterial', {
                manufacturingFileId: this.$route.query.manufacturingFileId,
                entryId,
                rawMaterialId: entry.id,
                material_id: entry.material_id,
                warehouse_id: entry.warehouse_id,
                count: entry.count,
                quantity: {
                    quantity: entry.quantity,
                    unit_size_id
                },
                unit_size_id,
                cost: {
                    amount: entry.cost.amount,
                    currency_id: this.configs.financial_file.default_currency_id
                },
                manufacturing_file_entry_id: entryId
            })
            .then(() => {
                payload.deleteRowIndex();
                Promise.all([
                    this.fetchAllRawMaterials()
                    .then(() => {
                        payload.connectionEnded(1, 'BeeAutocomplete');
                        this.loading = false;
                    }),
                    this.fetchEntryById()
                ])
            })
            .catch(() => {
                payload.connectionEnded(1, 'BeeAutocomplete');
                this.loading = false;
            })
        },

        updateLocalMaterialEdit(event){
            if (event.header.value === 'quantity.quantity'){
                this.rawMaterials[event.index].quantity = event.value;
                
                // update total cost
                this.rawMaterials[event.index].total_cost.amount =
                   Number(event.value) * Number(this.rawMaterials[event.index].cost.amount)
            }
            else if (event.header.value === 'cost'){
                // update total cost
                this.rawMaterials[event.index].total_cost.amount =
                   Number(this.rawMaterials[event.index].quantity) * Number(event.value.amount)
            }
            else {
                this.rawMaterials[event.index][event.header.value] = event.value
            }
        },

        resetEditedRow(){
            this.fetchAllRawMaterials();
        },

        confirmDeleteRawMaterial(entry){
            this.deleteRawMaterialDialog = true;
            this.deleteRawMaterialId = entry.id;
        },

        deleteRawMaterial(){
            this.loading = true;
            this.$store.dispatch('manufacturing/deleteRawMaterial', {
                manufacturingFileId: Number(this.$route.query.manufacturingFileId),
                entryId: Number(this.$route.query.entryId),
                rawMaterialId: this.deleteRawMaterialId,
            })
            .then(() => {
                this.fetchAllRawMaterials()
                .finally(() => {
                    this.loading = false;
                })
            })
            .catch(() => {
                this.loading = false;
            })
        },

        handlBlurTable(entry){
            this.tableWarehouseId = entry.warehouse_id;
            this.messagesSelectRowValidation = entryDetectedValidation(entry);
        },
        onChange(){
            if (this.materialId)
            {
                var material = this.materials.find(c => c.id == this.materialId);
                this.unitSize = this.unitSizes.find(c => c.id == material.unit_size_id)
                if(!this.warehouseId)
                    this.warehouseId = this.warehouses.find(c => c.id == material.warehouse_id).id
            }
        },
        onWarehouseChange(){
            this.unitSize = { id: null, name: null }
        }
    },

    async beforeRouteEnter(to, from, next) {
        let promises = [];
        let materials = store.state.materials.materials;
        if(Object.keys(materials).length === 0){
            promises.push(store.dispatch("materials/fetchAll"));
        }

        let warehouses = store.state.warehouses.warehouses;
        if(Object.keys(warehouses).length === 0){
            promises.push(store.dispatch("warehouses/fetchAll"));
        }

        let unitSizes = store.state.unitSizes.unit_sizes;
        if(Object.keys(unitSizes).length === 0){
            promises.push(store.dispatch("unitSizes/fetchAll"));
        }

		if (to.name === 'edit-product' || to.name === 'show-product'){
            promises.push(
                store.dispatch('manufacturing/fetchEntryById', {
                    manufacturingFileId: to.query.manufacturingFileId,
                    entryId: to.query.entryId
                })
            );
			promises.push(
                store.dispatch('manufacturing/fetchAllRawMaterials', {
                    manufacturingFileId: to.query.manufacturingFileId,
                    entryId: to.query.entryId
                })
            );
		}
		await Promise.all(promises)
		next(vm => {
            if (to.name === 'edit-product' || to.name === 'show-product') {
                const entry = store.state.manufacturing.entry;
                const materials = store.state.materials.materials;
                const warehouses = store.state.warehouses.warehouses;
                
                // fill to edit
                vm.warehouseId = 
                    warehouses
                    .find(c => c.id === materials.find(c => c.id === entry.material_id).warehouse_id)
                    .id;
                vm.materialId = entry.material_id;
                vm.producedQuantity = entry.quantity.quantity;
                vm.count = entry.count;
                vm.unitSize = store.state.unitSizes.unit_sizes.find(c => c.id === materials.find(c => c.id === entry.material_id)?.unit_size_id);
                vm.lossRatio = entry.loss_ratio;
                vm.totalCostAmount = entry.total_cost.amount;

                EventBus.$emit('initialize-last-row', { warehouse_id: vm.warehouseId });
            }
            else {
                vm.warehouseId = store.state.configs.configs.warehouse.id;
                vm.tableWarehouseId = store.state.configs.configs.warehouse.id;
                // vm.unitSizeId = store.state.configs.financial_file;
                EventBus.$emit('initialize-last-row', { warehouse_id: store.state.configs.configs.warehouse.id });
            }
        });
	},

    mounted(){
        if(this.$route.name === 'edit-product' || this.$route.name === 'show-product') {
            window.observer.observe(document.querySelector("#tool-bar"));
            window.observer.observe(document.querySelector("#footer"));
        }
    },

    beforeDestroy(){
        this.$store.dispatch('manufacturing/resetRawMaterial')
    }
}
</script>

<style scoped>
.border {
	border: 1px solid #f1f1f1;
    border-radius: 8px;
}
</style>